<script setup lang="ts">
import type { UseHardwareHistoryItem } from '~/store-v2/installation/useHardwareHistory'
import { SolModal } from '@solfacil/girassol'
import IconDelete from '~icons/material-symbols/delete-outline'
import EquipmentDetailRow from '~/components/customer/shared/EquipmentDetailRow.vue'

const props = withDefaults(defineProps<{
  isOpen: boolean
  updateEquipment: NonNullable<UseHardwareHistoryItem>['target_hardware_state'] | null
  isPendingContractCancel: boolean
  isPendingContractResend: boolean
  // Blocks resend/cancel of the pending TAF; details stay visible
  readOnly?: boolean
}>(), {
  readOnly: false,
})

const emit = defineEmits<{
  close: []
  resendTerm: []
  cancelUpdateEquipment: []
}>()

const { t } = useI18n()

function handleResendTerm() {
  if (props.readOnly)
    return

  emit('resendTerm')
}

function handleCancelUpdateEquipment() {
  if (props.readOnly)
    return

  emit('cancelUpdateEquipment')
}
</script>

<template>
  <SolModal
    id="update-equipment-details-modal"
    :is-open="isOpen"
    :title="t('customer.equipment_update.modal.details_title')"
    :size="{ desktop: 'medium', mobile: 'bottom-sheet' }"
    @close="emit('close')"
  >
    <div v-if="updateEquipment" class="flex flex-col gap-6">
      <p class="fonts-body-medium-bold text-neutral-low-medium">
        {{ t('customer.equipment_update.modal.details_description') }}
      </p>

      <p class="fonts-body-medium-regular text-neutral-low-medium">
        {{ t('customer.equipment_update.modal.details_subtitle') }}
      </p>

      <div class="flex flex-wrap">
        <EquipmentDetailRow
          label="Concessionária"
          :value="updateEquipment.distributor?.name || '-'"
        />

        <EquipmentDetailRow
          label="Fase do local da instalação"
          :value="updateEquipment.phase?.name || '-'"
          margin-top
        />
      </div>

      <EquipmentDetailRow
        label="Marca e modelo do módulos"
        secondary-label="Quantidade de módulos"
        :value="updateEquipment.modules"
      />

      <EquipmentDetailRow
        label="Tipo de inversor"
        :value="
          updateEquipment.inverter_type === 'microinverter'
            ? 'Microinversor'
            : 'Normal (string)'
        "
      />

      <EquipmentDetailRow
        v-if="
          updateEquipment.inverter_type === 'microinverter'
            && updateEquipment.microinverters?.length
        "
        label="Marca e modelo do microinversor"
        secondary-label="Quantidade de microinversores"
        :value="updateEquipment.microinverters"
      />

      <EquipmentDetailRow
        v-if="
          updateEquipment.inverter_type === 'string'
            && updateEquipment.inverters?.length
        "
        label="Marca e modelo do inversor"
        secondary-label="Quantidade de inversores"
        :value="updateEquipment.inverters"
      />
    </div>
    <template #action-primary>
      <SolButton
        id="resend-update-equipment-term"
        variant="primary"
        size="medium"
        :disabled="readOnly || isPendingContractResend"
        @click="handleResendTerm"
      >
        {{ t('customer.equipment_update.modal.details_primary') }}
      </SolButton>
    </template>
    <template #action-tertiary>
      <SolButton
        id="cancel-update-equipment"
        variant="tertiary"
        size="medium"
        :disabled="readOnly || isPendingContractCancel"
        @click="handleCancelUpdateEquipment"
      >
        <template #icon:left>
          <IconDelete />
        </template>
        {{ t('customer.equipment_update.button.cancel') }}
      </SolButton>
    </template>
  </SolModal>
</template>
